import { Illustration, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import {
  INTERVIEW_EXPERIENCE_RESULT,
  type InterviewExperienceResult,
} from "../../api/useUpdateInterview";
import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";
import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

const EXPERIENCE_OPTIONS = [
  {
    label: "I didn\u2019t interview",
    value: INTERVIEW_EXPERIENCE_RESULT.didNotInterview,
    nextStep: FEEDBACK_STEP.notInterview,
  },
  {
    label: "They weren\u2019t expecting me, but interviewed me anyway",
    value: INTERVIEW_EXPERIENCE_RESULT.notExpectedButInterviewed,
    nextStep: FEEDBACK_STEP.workplace,
  },
  {
    label: "They expected me, but I waited 15+ minutes",
    value: INTERVIEW_EXPERIENCE_RESULT.expectedButWaited,
    nextStep: FEEDBACK_STEP.workplace,
  },
  // Using \u00A0 (non-breaking space) ensures space between text and emoji is preserved
  {
    label: "They expected me and it went smoothly\u00A0\u00A0\u{1F389}",
    value: INTERVIEW_EXPERIENCE_RESULT.expectedAndSmooth,
    nextStep: FEEDBACK_STEP.workplace,
  },
] as const;

interface InterviewExperienceStepContentProps {
  interview: InterviewWithDetails;
  placementCandidate: PlacementCandidate;
  onExperienceSelected: (experience: InterviewExperienceResult, nextStep: FeedbackStep) => void;
}

export function InterviewExperienceStepContent(props: InterviewExperienceStepContentProps) {
  const { interview, placementCandidate, onExperienceSelected } = props;

  const source = useInterviewAttendanceSource();

  const eventContext = {
    placementCandidate,
    interview,
    source,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_EXPERIENCE_VIEWED, eventContext);

  function handleOptionClick(option: (typeof EXPERIENCE_OPTIONS)[number]) {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_EXPERIENCE_OPTION_CLICKED, {
      ...eventContext,
      option: option.value,
    });

    onExperienceSelected(option.value, option.nextStep);
  }

  return (
    <>
      <Stack
        spacing={7}
        alignItems="center"
        justifyContent="center"
        sx={{ paddingX: 8, paddingY: 6 }}
      >
        <Illustration type="facility" />
        <Stack spacing={6} textAlign="center">
          <Text variant="h2">How did your interview go?</Text>
          <Text>
            Let us know what happened at{" "}
            <Box sx={{ fontWeight: 800 }} component="span">
              {interview.workplace?.name}
            </Box>{" "}
            so we can keep things moving.
          </Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 2 }}>
        <Stack spacing={4} width="100%">
          {EXPERIENCE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant="outlined"
              size="large"
              onClick={() => {
                handleOptionClick(option);
              }}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      </DialogFooter>
    </>
  );
}
